/**
 * Dashboard API Endpoints (RTK Query)
 * 
 * Silent background refresh with no UI flicker:
 * - RTK Query keeps `data` populated during refetch
 * - isFetching flag indicates background activity (optional loading indicator)
 * - refetchOnMountOrArgChange: true ensures fresh data on every mount
 */

import { baseApi } from '../../../../store/api/baseApi';

// Response types
interface TeacherAttendanceResponse {
  success: boolean;
  totalTeacherAttendance: number;
}

interface StudentTotalResponse {
  success: boolean;
  data: {
    totalStudents?: number;
    totalEnrollment?: number;
    totalPresent?: number;
    totalAbsent?: number;
  };
  message?: string;
}

interface TotalRegisteredEmpResponse {
  success: boolean;
  totalRegisteredEmp: number;
  totalContractEmpCount: number;
  totalEmployee: number;
  message: string;
}

interface PMSHRIResponse {
  success: boolean;
  data: {
    totalPMSHRI: number;
  };
  message?: string;
}

interface StudentPerformanceResponse {
  success: boolean;
  academic_year: number;
  totalStudents: number;
  totalStudentsWithstatus0?: number;
  totalStudentsWithstatus1?: number;
  message?: string;
}

export interface Result10To12SummaryResponse {
  success: true;
  data: {
    title: string;
    year: number;
    class10: { totalStudents: number; /* ... */ };
    class12: { totalStudents: number; /* ... */ };
  };
}

export const dashboardApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    // KVS Samagam data
    getKvsSamagam: build.query<{ totalSchools: number; totalKVs: number } | null, void>({
      query: () => '/kvis/total',
      transformResponse: (response: any) => {
        if (response && response.success && response.data) {
          return {
            totalSchools: response.data.schoolCount || response.data.totalSchools || 0,
            totalKVs: response.data.kvCount || response.data.totalKVs || 0,
          };
        }
        return null;
      },
      providesTags: ['KVS'],
      // Cache for 5 minutes - data doesn't change frequently
      keepUnusedDataFor: 300,
    }),

    // Teacher attendance total
    getTotalTeacherAttendance: build.query<number | null, void>({
      query: () => {
        const currentYear = new Date().getFullYear();
        const yearParam = currentYear.toString().slice(-2); // Get last 2 digits (e.g., 2026 -> 26)
        return `/pis/totalteacherattendance?year=${yearParam}`;
      },
      transformResponse: (response: TeacherAttendanceResponse) => {
        if (response && response.success && typeof response.totalTeacherAttendance === 'number') {
          return response.totalTeacherAttendance;
        }
        return null;
      },
      providesTags: ['Attendance'],
      // Cache for 5 minutes - attendance summary doesn't need real-time updates
      keepUnusedDataFor: 300,
    }),

    // Student total
    getTotalStudents: build.query<StudentTotalResponse['data'] | null, void>({
      query: () => {
        const today = new Date().toISOString().split('T')[0];
        return `/sis/totalclasswisesectiondata?fromdate=${today}&todate=${today}`;
      },
      transformResponse: (response: StudentTotalResponse) => {
        if (response && response.success && response.data) {
          return response.data;
        }
        return null;
      },
      providesTags: ['Student'],
      // Cache for 5 minutes
      keepUnusedDataFor: 300,
      
    }),

    // Total Registered Employees (Teachers count)
    // 1 hour cache (3600 seconds) - updates immediately on data change via cache invalidation
    getTotalRegisteredEmp: build.query<TotalRegisteredEmpResponse | null, void>({
      query: () => '/pis/totalregisteredemp',
      transformResponse: (response: TotalRegisteredEmpResponse) => {
        if (response && response.success) {
          return response;
        }
        return null;
      },
      providesTags: ['Employee'],
      // Keep cached data for 1 hour (3600 seconds)
      keepUnusedDataFor: 3600,
    }),

    // Total PMSHRI count
    getTotalPMSHRI: build.query<{ totalPMSHRI: number } | null, void>({
      query: () => '/pmshri/totalpmshri',
      transformResponse: (response: PMSHRIResponse) => {
        if (response && response.success && response.data) {
          return {
            totalPMSHRI: response.data.totalPMSHRI || 0
          };
        }
        return null;
      },
      providesTags: ['Dashboard'],
      // Cache for 5 minutes
      keepUnusedDataFor: 300,
    }),

    getTotalStudentCountForPerformance: build.query<{ totalStudents: number } | null, void>({
      query: () => {
        // Academic year usually starts in April; Jan-Mar belongs to previous academic year.
        const now = new Date();
        const academicYear = 2025;
        return `/sis-model/totalstudents-hpc-national?academic_year=${academicYear}`;
      },
      transformResponse: (response: StudentPerformanceResponse) => {
        if (response && response.success) {
          return {
            totalStudents: response.totalStudents || 0,
          };
        }
        return null;
      },
      providesTags: ['Student'],
      // Cache for 5 minutes
      keepUnusedDataFor: 300,
    }),
    // Today's detailed attendance - region-wise with summary
    getTodaysDetailedAttendance: build.query<{ totalPresent: number; totalAbsent: number; totalEnrollment: number; overallAttendancePercentage: number } | null, void>({
      query: () => '/sis/todays-detailed-attendance',
      transformResponse: (response: any) => {
        if (response && response.success && response.summary) {
          return {
            totalPresent: response.summary.totalPresent || 0,
            totalAbsent: response.summary.totalAbsent || 0,
            totalEnrollment: response.summary.totalEnrollment || 0,
            overallAttendancePercentage: response.summary.overallAttendancePercentage || 0,
          };
        }
        return null;
      },
      providesTags: ['Student'],
      keepUnusedDataFor: 300,
    }),

    getResult10To12Summary: build.query<Result10To12SummaryResponse['data'] | null, number>({
      query: (year) => `/result/10-12-summary?year=${year}`,
      transformResponse: (response: Result10To12SummaryResponse) => response.success ? response.data : null,
    }),
  }),
  overrideExisting: false,
});

// Export auto-generated hooks
export const {
  useGetKvsSamagamQuery,
  useGetTotalTeacherAttendanceQuery,
  useGetTotalStudentsQuery,
  useGetTotalRegisteredEmpQuery,
  useGetTotalPMSHRIQuery,
  useGetTotalStudentCountForPerformanceQuery,
  useGetResult10To12SummaryQuery,
  useGetTodaysDetailedAttendanceQuery,
} = dashboardApi;
